/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 5 hook(s) need dependency review; fix each and remove this directive */
import { getBackendUrl } from "@Utils";
import { useTranslation, untranslated } from "@i18n";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import axiosWrapper from "@Services/axiosWrapper";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_SUCCESS, TOAST_TYPE_ERROR } from "@Services/constants";
import FileDelete from "../FileDelete/FileDelete";
import { saveCustomFieldsData, removeFileUploadData, updateFileUploadData } from "@Actions/fileUpload";
import ProgressBarComponent from "@CubeComponents/Mui_Form/ProgressBarComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import _ from "lodash";
import useCustomFields, { NomenclatureField } from "@Services/utils/useCustomFields";
import { MdDeleteOutline, MdOutlineEdit } from "react-icons/md";
import { FaRegSave } from "react-icons/fa";
import { BsExclamationCircle } from "react-icons/bs";
import Upload from "@Assets/icons/svg/upload.svg";
import { useNavigate } from "react-router-dom";
import { FormQuestionFields, useForms, addValidationToForms, useFormValidator } from "@Features/forms";

import { isFileNomenclatureCorrect } from "@Services/utils/helpers";
import { getFileImage } from "@Services/utils/iconMap";
import type { UploadEscrowFileResponse } from "@ServerTypes/escrow/escrow.schema";
import type { UpdateFileNomenclatureResponse } from "@ServerTypes/fileOperations/fileOperations.schemas";
import * as React from "react";
import FilesTable from "./FilesTable";
import ReconciliationBanner from "./ReconciliationBanner";
import ReconciliationLogModal from "./ReconciliationLogModal";
import { getReconciliationCounts, getReconciliationRows } from "./reconciliationHelper";
import {
  MANDATORY_FIELDS_ERROR,
  REVISION_NOT_IN_SERIES_ERROR,
  REVISION_REQUIRED_ERROR,
  REVISION_RULE_ERROR_MESSAGES,
  REVISION_ERROR_SET,
  invalidFieldError,
  parseDateFieldValue,
} from "@Components/ExcelImport/ExcelHelper";
import {
  validateRevisionAgainstRules,
  REVISION_SEQUENCE_TYPES,
  type RevisionSequenceRules,
  type RevisionSequenceType,
} from "../../../../../../../helpers/revisionPattern";
import { errorCodes } from "../../../../../../../helpers/errorCodes";
import AutoComplete from "@CubeComponents/Mui_Form/AutoComplete";
import TextField from "@mui/material/TextField";
import { RootState } from "@Reducers/index";
import type { ComponentProps } from "react";
import type { ExcelImportContainer, FileUploadItem } from "@Reducers/minimiseFileUpload";
import type { FormTemplateField as UploadTemplateSection, QuestionField } from "@ServerTypes/forms/forms.schema";

/**
 * A row viewed as a bag of dynamic keys, for the merged patches this component builds itself (object
 * literals, so no assertion is involved). Rows that come back out of the store are read with
 * `readRowField` instead — see its note.
 */
type UploadRowFields = FileUploadItem & Record<string, unknown>;

/**
 * Read a runtime-computed key off a stored upload row: a custom-field answer (the reducer merges each
 * one onto the row under its question `_id`) or one of the three `uploadType.*` flags. `FileUploadItem`
 * declares neither, so `row[key]` would need a cast claiming the row has an index signature;
 * `Reflect.get` reads the same property and keeps the result `unknown`.
 */
const readRowField = (row: FileUploadItem | undefined, key: string): unknown =>
  row ? Reflect.get(row, key) : undefined;

/**
 * A patch handed to `updateFileData` / `updateFileUploadData`. Wider than `Partial<FileUploadItem>`
 * because `handleUpload` writes an `uploadType.*` key computed at runtime.
 */
type UploadRowPatch = Partial<FileUploadItem> & Record<string, unknown>;

/** A row of the grid: the stored item plus the 1-based `id` and `_id` this component stamps on. */
type UploadGridRow = FileUploadItem & { id: number; _id?: string };

/**
 * The synthetic bulk-edit row (grid id `0`), plus whatever answers get typed into its cells. Declared
 * as a real grid row so it can sit in `visibleFiles` next to the stored ones without being asserted
 * into one: it carries only the four members the grid reads off it, all of which a row may have.
 */
type BulkEditRow = UploadGridRow & {
  id: number;
  progress: number;
  name: string;
  isBulkEdit: true;
} & Record<string, unknown>;

/** The revision metadata a matched Excel / direct-import container carries, once checked. */
type MatchedContainerRevision = {
  revisionLabel?: string;
  previousLabel?: string;
  startPattern?: string;
  existingLabels: string[];
  revisionSeriesId?: string;
  sequenceRules: RevisionSequenceRules | null;
  sequenceType: RevisionSequenceType | null;
};

const REVISION_RULE_KEYS = [
  "allowSkipFirst",
  "allowSkipIntermediate",
  "allowAnyOrder",
  "noRepeatedRevisions",
  "revisionMandatory",
] as const satisfies readonly (keyof RevisionSequenceRules)[];

const asString = (value: unknown): string | undefined => (typeof value === "string" ? value : undefined);

/** The stored rules are whatever the container API put in the bag; only the five declared flags are read. */
const readSequenceRules = (value: unknown): RevisionSequenceRules | null => {
  if (typeof value !== "object" || value === null) return null;
  const rules: RevisionSequenceRules = {};
  for (const key of REVISION_RULE_KEYS) {
    const flag: unknown = Reflect.get(value, key);
    if (typeof flag === "boolean") rules[key] = flag;
  }
  return rules;
};

/**
 * Both container fields on `FileUploadItem` are declared `[key: string]: unknown` — they carry the
 * Excel import's own columns and the container API's reply, not a schema — so the revision metadata is
 * checked field by field on the way out instead of the whole bag being asserted into a shape.
 */
const readRevisionMeta = (
  container: FileUploadItem["matchedContainer"] | FileUploadItem["matchedDirectImportContainer"],
): MatchedContainerRevision | undefined => {
  if (!container) return undefined;
  const existingLabels: unknown = container.existingLabels;
  const sequenceType: unknown = container.sequenceType;
  return {
    revisionLabel: asString(container.revisionLabel),
    previousLabel: asString(container.previousLabel),
    startPattern: asString(container.startPattern),
    existingLabels: Array.isArray(existingLabels)
      ? existingLabels.filter((label): label is string => typeof label === "string")
      : [],
    revisionSeriesId: asString(container.revisionSeriesId),
    sequenceRules: readSequenceRules(container.sequenceRules),
    sequenceType: REVISION_SEQUENCE_TYPES.find((type) => type === sequenceType) ?? null,
  };
};

/** The template/row callbacks are forwarded verbatim to `FilesTable`, so they take its shapes. */
type FilesTableProps = ComponentProps<typeof FilesTable>;

interface FilesGridProps {
  template?: UploadTemplateSection[];
  formTemplateId?: string;
  /** `FileUpload` passes a branded space id; the escrow flow passes `string | null`. */
  workspaceId?: string | null;
  setEnableDone?: (enabled: boolean) => void;
  /**
   * The folder's nomenclature RULE. NOTE: `CurrentFileUploadPropertiesState.fileNomenclature`
   * declares `string | null`, which is wrong — this component's own `defaultProps` below supply
   * `{ fields: [], separator: "-" }` and every read here treats it as that object.
   */
  fileNomenclature?: FilesTableProps["fileNomenclature"];
  /**
   * Set only by the escrow surface (`components/Escrow/EscrowFiles`); `FileUpload` never passes it,
   * so every `batchId ? … : …` branch below is the escrow-vs-normal-upload switch.
   */
  batchId?: string;
  formValidator?: ReturnType<typeof useFormValidator>;
  isDirectImportFlow?: boolean;
  directImportContainers?: ExcelImportContainer[];
}

const FilesGrid = ({
  template,
  formTemplateId,
  workspaceId,
  setEnableDone,
  fileNomenclature,
  batchId,
  formValidator,
  isDirectImportFlow,
  directImportContainers,
}: FilesGridProps) => {
  const fileUploadList = useSelector((state: RootState) => state.fileUploadList);
  const hideUploadAsNewFile = useSelector((state: RootState) => !!state.layout.FILE_MANAGER?.HIDE_UPLOAD_AS_NEW_FILE);
  const onlyUploadAsNewVersion = useSelector(
    (state: RootState) => !!state.layout.FILE_MANAGER?.ONLY_UPLOAD_AS_NEW_VERSION,
  );
  const { t } = useTranslation();
  const uploadType = {
    UPLOAD_AS_NEW_FILE: "uploadAsNewFile",
    UPLOAD_AS_NEW_VERSION: "uploadAsNewVersion",
    UPLOAD_AS_NEW_VERSION_RETAIN_FIELDS: "uploadAsNewVersionRetainFields",
  };
  const dispatch = useDispatch();

  const updateFileData = (id: number, updater: UploadRowPatch | ((current: FileUploadItem) => UploadRowPatch)) => {
    const index = id - 1;
    const current: FileUploadItem = fileUploadList[index] || {};
    const data = typeof updater === "function" ? updater(current) : updater;
    dispatch(
      updateFileUploadData({
        index,
        data,
      }),
    );
  };

  const [showReconLogModal, setShowReconLogModal] = useState(false);
  const reconciliationCounts = React.useMemo(
    () => (isDirectImportFlow ? getReconciliationCounts(fileUploadList, directImportContainers) : null),
    [isDirectImportFlow, fileUploadList, directImportContainers],
  );

  const reconciliationRows = React.useMemo(
    () => (showReconLogModal ? getReconciliationRows(fileUploadList, directImportContainers) : null),
    [showReconLogModal, fileUploadList, directImportContainers],
  );

  const [openDeleteConfirmationModal, setOpenDeleteConfirmationModal] = useState(false);
  const isFileVersionUploading = useSelector((state: RootState) => state.isFileVersionUploading);
  const [selectedFileIds, setSelectedFileIds] = useState<number[]>([]);
  const [isBulk, setIsBulk] = useState(false);
  const [bulkData, setBulkData] = useState<BulkEditRow>({
    id: 0,
    progress: 0,
    name: "",
    isBulkEdit: true,
  });
  const [bulkActionType, setBulkActionType] = useState<string | null>(null);
  const [bulkActionApplied, setBulkActionApplied] = useState(false);
  const [bulkActionInProgress, setBulkActionInProgress] = useState(false);
  const pendingFieldsRef = useRef<Map<number, { _id: string; value: unknown }[]>>(new Map());
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const [flushTrigger, setFlushTrigger] = useState(0);
  const [gridRefreshKey, setGridRefreshKey] = useState(0);
  const [pendingApiCalls, setPendingApiCalls] = useState(0);

  const { validateFormWithId } = useCustomFields();

  const navigate = useNavigate();

  const {
    handleURLModalState,
    isFileUploading,
    handleFileUploadChange,
    removeUserFromList,
    saveEditedImage,
    handleRemoveUploadedFile,
    openAddUserModal,
    handleInputChange,
    handleSaveUrlData,
    showUrlModal,
    spaceId,
    showGeoLocationModal,
    setShowGeoLocationModal,
    handleGeoLocationModalState,
    closeUsersModal,
    showAddUsersModal,
    handleAddUserClick,
    // KNOWN BUG, preserved verbatim: `useForms` does not return `spaceId`, so the value destructured
    // here is always `undefined` and is forwarded as such to every `FormQuestionFields` below. The
    // assertion states the gap rather than hiding it behind the `as any` that used to be here.
  } = useForms({ navigate }) as ReturnType<typeof useForms> & { spaceId?: string };

  const filesData = React.useMemo(
    () =>
      fileUploadList.map(
        (file: FileUploadItem, index: number): UploadGridRow => ({
          ...file,
          id: index + 1,
          _id: file.fileId,
        }),
      ),
    [fileUploadList],
  );

  const visibleFiles = React.useMemo(() => {
    const base = filesData.filter((f) => !f.isDeleted);
    return isBulk ? [bulkData, ...base] : base;
  }, [filesData, isBulk, bulkData]);

  const selectedFiles = React.useMemo(
    () => filesData.filter((file) => !file.isDeleted).filter((file) => selectedFileIds.includes(file.id)),
    [filesData, selectedFileIds],
  );
  const hasDuplicateSelected = React.useMemo(() => selectedFiles.some((file) => file.isDuplicate), [selectedFiles]);

  const disableDeleteButton = selectedFileIds.length === 0;
  const disableBulkUploadButton = disableDeleteButton || hasDuplicateSelected;
  const disableBulkEditButton = !filesData.some((file) => !file.isDeleted);

  const allFilesCompleted = React.useMemo(() => {
    return visibleFiles.every((file) => {
      const item = fileUploadList[file.id - 1];
      if (!item || item.isDeleted) return true;
      return item.progressCompleted || item.error || item.isBulkEdit;
    });
  }, [visibleFiles, fileUploadList]);

  const disableBulkActionDropdown = !allFilesCompleted || pendingApiCalls > 0 || bulkActionApplied;

  useEffect(() => {
    const rows = visibleFiles;
    addValidationToForms({
      // Optional on the props above only because `defaultProps` declares it so; `FileUpload` always
      // passes a real validator. Erased assertion at the single boundary that requires it.
      formValidator: formValidator!,
      template,
      rows,
    });
  }, [visibleFiles.length, isBulk]);

  const recalculateErrors = (fileData: FileUploadItem) => {
    const errors: string[] = [];

    if (fileData.isFileNomenclatureCorrect === false) {
      errors.push("File name does not comply with the nomenclature set for this folder");
    }
    if (fileData.duplicateFileStatus?.isDuplicateContainer) {
      errors.push("Duplicate Containers exist for this file");
    }
    if (fileData.duplicateFileStatus?.noMatchingContainer) {
      errors.push("File name doesn't match any data in the imported file");
    }

    const stalePerRowErrors = new Set<string>([MANDATORY_FIELDS_ERROR, ...REVISION_ERROR_SET]);
    template?.forEach((section) => {
      section.questionFields?.forEach((field) => {
        // `questionValue` is `.optional()` on the schema but every rendered question has one, and
        // `invalidFieldError` takes a required string. Erased assertion.
        stalePerRowErrors.add(invalidFieldError(field.questionValue!));
      });
    });
    const carriedErrors: string[] =
      fileData.matchedDirectImportContainer?.containerData?.errors ?? fileData.matchedContainer?.errors ?? [];
    carriedErrors.forEach((msg: string) => {
      if (!stalePerRowErrors.has(msg) && !errors.includes(msg)) errors.push(msg);
    });

    if (fileData.revisionRejected && fileData.error && !errors.includes(fileData.error)) {
      errors.push(fileData.error);
    }

    const matched = readRevisionMeta(fileData.matchedDirectImportContainer ?? fileData.matchedContainer);
    const revisionLabel = matched?.revisionLabel;
    const previousLabel = matched?.previousLabel;
    const startPattern = matched?.startPattern;
    const existingLabels = matched?.existingLabels ?? [];
    const isUploadAsNewFile = !!readRowField(fileData, uploadType.UPLOAD_AS_NEW_FILE);
    if (revisionLabel) {
      // `revisionLabel` is read off `matched`, so reaching here means the container is present.
      if (!matched.revisionSeriesId) {
        errors.push(REVISION_NOT_IN_SERIES_ERROR);
      } else if (startPattern) {
        const violation = validateRevisionAgainstRules({
          label: revisionLabel,
          startPattern,
          previousLabel: isUploadAsNewFile ? null : (previousLabel ?? null),
          existingLabels: isUploadAsNewFile ? [] : existingLabels,
          rules: matched.sequenceRules,
          sequenceType: matched.sequenceType,
        });
        if (violation) {
          const message = REVISION_RULE_ERROR_MESSAGES[violation];
          if (!errors.includes(message)) errors.push(message);
        }
      }
    } else if (matched && matched.sequenceRules?.revisionMandatory) {
      if (!errors.includes(REVISION_REQUIRED_ERROR)) errors.push(REVISION_REQUIRED_ERROR);
    }

    if (!template?.length) return errors;

    const mandatoryMissing: string[] = [];
    template.forEach((section) => {
      section.questionFields?.forEach((field) => {
        const value = readRowField(fileData, field._id);
        const isEmpty =
          value === undefined || value === null || value === "" || (Array.isArray(value) && value.length === 0);
        if (field.isMandatory && isEmpty) {
          mandatoryMissing.push(field.questionValue!);
        }
        if (isEmpty) return;
        if (field.fieldType === "multi-select") {
          const arr = Array.isArray(value) ? value : [value];
          if (!arr.every((item: string) => field.answerChoices?.includes(item))) {
            errors.push(invalidFieldError(field.questionValue!));
          }
        } else if (field.fieldType === "single-select") {
          // `answerChoices` is `string[]`; the answer read off the row is `unknown`. The array is
          // widened rather than the value asserted — `includes(<not a choice>)` returning `false` is
          // exactly the intent.
          if (!(field.answerChoices as unknown[] | undefined)?.includes(value))
            errors.push(invalidFieldError(field.questionValue!));
        } else if (field.fieldType === "date-time") {
          const isParsed = value instanceof Date || (typeof value === "number" && Number.isFinite(value));
          if (!isParsed && parseDateFieldValue(value, field.displayAs) === null) {
            errors.push(invalidFieldError(field.questionValue!));
          }
        } else if (field.fieldType === "number") {
          const num = Number(value);
          const max = field.maxFieldRange || Infinity;
          const min = field.minFieldRange || -Infinity;
          if (Number.isNaN(num) || num < min || num > max) errors.push(invalidFieldError(field.questionValue!));
        } else if (field.fieldType === "single-line" || field.fieldType === "multi-line") {
          const max = field.maxFieldLimit || Infinity;
          const min = field.minFieldLimit || 0;
          if (typeof value === "string" && (value.length < min || value.length > max)) {
            errors.push(invalidFieldError(field.questionValue!));
          }
        }
      });
    });
    if (mandatoryMissing.length > 0) {
      errors.unshift(MANDATORY_FIELDS_ERROR);
    }
    return errors;
  };

  useEffect(() => {
    fileUploadList.forEach((file: FileUploadItem, index: number) => {
      if (file.customFieldsAutoFilled) return;

      if (isDirectImportFlow && template?.length) {
        const containerData = file.matchedDirectImportContainer?.containerData;

        if (containerData) {
          const customFieldUpdates: Record<string, unknown> = {};

          template.forEach((section) => {
            section.questionFields?.forEach((field) => {
              const questionValue = field.questionValue!;
              if (containerData[questionValue] !== undefined && containerData[questionValue] !== "") {
                customFieldUpdates[field._id] = containerData[questionValue];
              }
            });
          });

          const errors = recalculateErrors({ ...file, ...customFieldUpdates });
          if (Object.keys(customFieldUpdates).length > 0) {
            dispatch(
              updateFileUploadData({
                index,
                data: {
                  ...customFieldUpdates,
                  customFieldsAutoFilled: true,
                  customFieldsUpdated: true,
                  errors,
                },
              }),
            );
          } else {
            dispatch(
              updateFileUploadData({
                index,
                data: { customFieldsAutoFilled: true, errors },
              }),
            );
          }
        } else {
          const errors = recalculateErrors(file);
          dispatch(
            updateFileUploadData({
              index,
              data: { customFieldsAutoFilled: true, errors },
            }),
          );
        }
      } else {
        const errors = recalculateErrors(file);
        dispatch(
          updateFileUploadData({
            index,
            data: { customFieldsAutoFilled: true, errors },
          }),
        );
      }
    });
  }, [fileUploadList.length, isDirectImportFlow, template]);

  useEffect(() => {
    if (flushTrigger === 0) return;
    const allPending = new Map(pendingFieldsRef.current);
    pendingFieldsRef.current = new Map();
    allPending.forEach((fieldsData, id) => {
      saveFormTemplate(id, fieldsData, fileUploadList[id - 1]?.progressCompleted);
    });
  }, [flushTrigger]);

  const gridNameTemplate: FilesTableProps["gridNameTemplate"] = (file, id) => {
    const fileData: FileUploadItem = fileUploadList[id - 1] || {};
    const error = fileData.error || "";

    let fileValidationTooltip: React.ReactNode = null;

    if (
      !isFileVersionUploading &&
      (fileData.isDuplicate || fileData.duplicateFileStatus?.found) &&
      !fileData.duplicateFileStatus?.isDuplicateContainer &&
      !fileData.duplicateFileStatus?.noMatchingContainer &&
      !fileData.nomenclature?.length &&
      !readRowField(fileData, uploadType.UPLOAD_AS_NEW_FILE) &&
      !readRowField(fileData, uploadType.UPLOAD_AS_NEW_VERSION) &&
      !readRowField(fileData, uploadType.UPLOAD_AS_NEW_VERSION_RETAIN_FIELDS)
    ) {
      fileValidationTooltip = (
        <ToolTipComponent
          showTipPointer
          position="bottom"
          content={
            file.isFolder
              ? t("Folder with same name already exists. Please rename and try again.")
              : t("File with same name already exists. Upload as new file or version.")
          }
        >
          <span>
            <BsExclamationCircle className="w-3 h-3 text-black" />
          </span>
        </ToolTipComponent>
      );
    }

    return (
      <div className="flex flex-col gap-1 w-full">
        <div className="flex items-center justify-between w-full gap-2">
          <div className="flex items-center min-w-0">
            {/* `getFileImage` is declared `any` (iconMap.ts:43) although every branch of it returns a
                URL string; checking that here keeps the promise verified rather than asserted. */}
            <img src={asString(getFileImage(file))} alt="file" className="w-5 h-5 shrink-0" />
            <ToolTipComponent position="bottom" content={file.name ? untranslated(file.name) : undefined}>
              <span className="ms-1 truncate text-sm">{file.name}</span>
            </ToolTipComponent>
          </div>
          {fileValidationTooltip}
        </div>
        {error &&
          !fileData.duplicateFileStatus?.isDuplicateContainer &&
          !fileData.duplicateFileStatus?.noMatchingContainer && (
            <div id={`error_text_${id}`} className="text-xs text-red-500 break-words">
              {error}
            </div>
          )}
      </div>
    );
  };

  const progressTemplate: FilesTableProps["progressTemplate"] = (file, id) => {
    const progress = file.progress || 0;

    let displayProgress = progress;
    if (file.progressCompleted) {
      displayProgress = 100;
    } else if (progress === 100) {
      displayProgress = 99;
    }

    return (
      <div className="flex items-center w-full">
        <ProgressBarComponent
          value={displayProgress}
          variant="large"
          width="100%"
          showProgressValue
          id={`progress_${id}`}
        />
      </div>
    );
  };

  const gridLocationTemplate: NonNullable<FilesTableProps["gridLocationTemplate"]> = (file, id) => {
    const fileData: FileUploadItem = fileUploadList[id - 1] || {};
    const location = fileData.uploadLocation || "/";

    return (
      <div className="flex items-center w-full min-w-0">
        <ToolTipComponent position="bottom" content={untranslated(location)}>
          <span className="text-sm truncate">{location}</span>
        </ToolTipComponent>
      </div>
    );
  };

  const gridNomenclatureTemplate: FilesTableProps["gridNomenclatureTemplate"] = (file, id) => {
    if (file.isFolder || !fileNomenclature?.fields?.length) {
      return null;
    }
    const nomenclature = file.nomenclature || [];
    return (
      <div className="flex gap-2 items-center w-full overflow-x-auto py-1">
        {fileNomenclature.fields.map((field, index: number) => {
          const value = nomenclature[index] || "";
          return (
            <NomenclatureField
              key={`${id}-${field._id}-${index}`}
              args={{ ...file, id }}
              field={field}
              index={index}
              value={value}
              handleNomenclatureChange={handleNomenclatureChange}
            />
          );
        })}
      </div>
    );
  };

  const gridCustomFieldTemplate: FilesTableProps["gridCustomFieldTemplate"] = (field, id) => {
    const placeholderText = `Enter ${field.questionValue}`;
    const fileData: FileUploadItem = id !== 0 ? fileUploadList[id - 1] || {} : bulkData;
    const value = readRowField(fileData, field._id!) || "";
    const processedValue = field.fieldType === "multi-select" ? asArray(value) : value;
    // `|| ""` above already put a value in place of a missing answer, so only the array case is left.
    const displayValue = Array.isArray(processedValue) ? processedValue.join(", ") : String(processedValue);

    const isCustomFieldDisabled =
      id !== 0 &&
      (readRowField(fileData, uploadType.UPLOAD_AS_NEW_VERSION) ||
        readRowField(fileData, uploadType.UPLOAD_AS_NEW_VERSION_RETAIN_FIELDS));

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
    };

    return (
      <form
        className="flex justify-start items-center w-full h-full"
        aria-labelledby={`field-${field._id}`}
        onSubmit={handleSubmit}
        title={displayValue || undefined}
      >
        <fieldset style={{ width: "100%" }} aria-labelledby={`field-${field._id}`}>
          <legend id={`field-${field._id}`} className="sr-only">
            {field.questionValue}
          </legend>
          <FormQuestionFields
            key={`cf-${field._id}-${id}-${fileData.customFieldsAutoFilled ? 1 : 0}`}
            extraParams={{ id }}
            eachField={{
              ...field,
              answer: { value: processedValue },
              placeholderText,
            }}
            disabled={!!isCustomFieldDisabled}
            width="100%"
            handleCustomFieldData={handleCustomFieldData}
            multiTextRows={1}
            handleURLModalState={handleURLModalState}
            isFileUploading={isFileUploading}
            handleFileUploadChange={handleFileUploadChange}
            removeUserFromList={removeUserFromList}
            saveEditedImage={saveEditedImage}
            handleRemoveUploadedFile={handleRemoveUploadedFile}
            openAddUserModal={openAddUserModal}
            handleInputChange={handleInputChange}
            handleSaveUrlData={handleSaveUrlData}
            showUrlModal={showUrlModal}
            spaceId={spaceId}
            formValidator={formValidator}
            showGeoLocationModal={showGeoLocationModal}
            setShowGeoLocationModal={setShowGeoLocationModal}
            handleGeoLocationModalState={handleGeoLocationModalState}
            closeUsersModal={closeUsersModal}
            showAddUsersModal={showAddUsersModal}
            handleAddUserClick={handleAddUserClick}
          />
        </fieldset>
      </form>
    );
  };

  const getIsUploadDisabled = (id: number): boolean => {
    const file = fileUploadList[id - 1];
    if (!file) return true;

    // `questionFields?.length` is `number | undefined`; the `!` keeps `> 0` on a number without the
    // `?? 0` that would change the emitted expression.
    const hasCustomFields = template?.some((section) => section.questionFields?.length! > 0);

    // The three `uploadType.*` keys are dynamic, so each read is `unknown`; the flags stored under
    // them are always `true`. Coerced rather than asserted, so the declared `boolean` is real.
    return Boolean(
      readRowField(file, uploadType.UPLOAD_AS_NEW_VERSION) ||
      readRowField(file, uploadType.UPLOAD_AS_NEW_FILE) ||
      readRowField(file, uploadType.UPLOAD_AS_NEW_VERSION_RETAIN_FIELDS) ||
      (hasCustomFields && !file.progressCompleted),
    );
  };

  const uploadOptions = [
    { value: "newFile", label: t("Upload as a New File") },
    { value: "newVersion", label: t("Upload as New version & override custom fields") },
    { value: "newVersionRetainFields", label: t("Upload as a new version & retain existing custom field data") },
  ];
  const visibleUploadOptions = onlyUploadAsNewVersion
    ? uploadOptions.filter((option) => option.value === "newVersion")
    : hideUploadAsNewFile
      ? uploadOptions.filter((option) => option.value !== "newFile")
      : uploadOptions;

  const gridUploadActionTemplate: FilesTableProps["gridUploadActionTemplate"] = (file, id) => {
    if (file.isBulkEdit && id === 0) {
      const hasEligibleFiles = fileUploadList.some((item) => isFileEligibleForBulkAction(item));
      if (!hasEligibleFiles) {
        return null;
      }

      const currentBulkValue = bulkActionType
        ? uploadOptions.find((opt) => opt.value === bulkActionType) || null
        : null;

      return (
        <div className="flex w-full justify-start">
          <AutoComplete
            options={visibleUploadOptions}
            getOptionLabel={(option) => option.label}
            value={currentBulkValue}
            disabled={disableBulkActionDropdown}
            onChange={(event, newValue) => {
              if (newValue) {
                setBulkActionType(newValue.value);
              } else {
                setBulkActionType(null);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={t("Select action for all")}
                size="small"
                sx={{
                  "& .MuiInputBase-root": {
                    backgroundColor: "#fff",
                  },
                }}
              />
            )}
            size="small"
            listStyle={{
              whiteSpace: "normal",
              height: "auto",
              overflow: "visible",
              textOverflow: "unset",
              lineHeight: "1.4",
              padding: "0.5rem 1rem",
            }}
            sx={{
              width: "100%",
              "& .MuiAutocomplete-input": {
                padding: "0.25rem 0.5rem !important",
              },
            }}
          />
        </div>
      );
    }

    const fileData = fileUploadList[id - 1];

    const localFile = { ...file };

    if (localFile.duplicateFileStatus?.found) {
      localFile.isDuplicate = true;
    }

    const isDisabled = getIsUploadDisabled(id);

    const uploadButton = (
      <div className="flex w-full justify-start">
        <ButtonComponent
          buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
          onClick={() => handleUpload({ id, name: localFile.name })}
        >
          <span className="flex items-center">{t("Upload")}</span>
        </ButtonComponent>
      </div>
    );

    const currentValue = readRowField(fileData, uploadType.UPLOAD_AS_NEW_FILE)
      ? uploadOptions.find((option) => option.value === "newFile")
      : readRowField(fileData, uploadType.UPLOAD_AS_NEW_VERSION)
        ? uploadOptions.find((option) => option.value === "newVersion")
        : readRowField(fileData, uploadType.UPLOAD_AS_NEW_VERSION_RETAIN_FIELDS)
          ? uploadOptions.find((option) => option.value === "newVersionRetainFields")
          : null;

    const uploadAsNewFileOrVersion = (
      <div className="flex w-full justify-start">
        <AutoComplete
          options={visibleUploadOptions}
          getOptionLabel={(option) => option.label}
          value={currentValue !== null ? currentValue : undefined}
          disabled={isDisabled}
          onChange={async (event, newValue) => {
            // `disableClearable` below: MUI never reports a cleared selection here, so `newValue` is
            // always one of `visibleUploadOptions`.
            const value = newValue.value;
            if (value === "newFile") {
              await handleUpload({ id, name: localFile.name }, uploadType.UPLOAD_AS_NEW_FILE);
            } else if (value === "newVersion") {
              await handleUpload({ id, name: localFile.name }, uploadType.UPLOAD_AS_NEW_VERSION);
            } else if (value === "newVersionRetainFields") {
              await handleUpload({ id, name: localFile.name }, uploadType.UPLOAD_AS_NEW_VERSION_RETAIN_FIELDS);
            }
            setGridRefreshKey((prev) => prev + 1);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={t("Select")}
              size="small"
              sx={{
                "& .MuiInputBase-root": {
                  backgroundColor: "#fff",
                },
              }}
            />
          )}
          size="small"
          disableClearable
          listStyle={{
            whiteSpace: "normal",
            height: "auto",
            overflow: "visible",
            textOverflow: "unset",
            lineHeight: "1.4",
            padding: "0.5rem 1rem",
          }}
          sx={{
            width: "100%",
            "& .MuiAutocomplete-input": {
              padding: "0.25rem 0.5rem !important",
            },
          }}
        />
      </div>
    );

    if (fileData?.duplicateFileStatus?.isDuplicateContainer || fileData?.duplicateFileStatus?.noMatchingContainer) {
      return null;
    }

    if (fileData?.nomenclature?.length && fileData.isFileNomenclatureCorrect && !batchId) {
      return null;
    } else if (localFile.isDuplicate) {
      if (batchId) {
        return !localFile.isFileNomenclatureCorrect || fileData?.nomenclature?.length
          ? uploadButton
          : uploadAsNewFileOrVersion;
      } else {
        return !localFile.isFileNomenclatureCorrect ? null : uploadAsNewFileOrVersion;
      }
    }

    return batchId ? uploadButton : null;
  };

  const uploadEscrowFile = async (id: number, data: Record<string, unknown>, showToast = true) => {
    const file = fileUploadList[id - 1];
    if (file?.uploaded) return false;

    try {
      updateFileData(id, { processing: true });

      const response = await axiosWrapper.post<UploadEscrowFileResponse>(
        `${getBackendUrl()}/escrow/uploadEscrowFile`,
        data,
      );

      if (response.data.status) {
        if (batchId) {
          dispatch(removeFileUploadData(id - 1));
        }

        if (showToast) {
          dispatch(
            showToastNotification({
              content: untranslated(response.data.message),
              type: TOAST_TYPE_SUCCESS,
            }),
          );
        }

        updateFileData(id, { uploaded: true });
        return true;
      } else {
        if (response.data.errorCode === errorCodes.REVISION_INVALID) {
          if (showToast) {
            dispatch(
              showToastNotification({
                content: untranslated(response.data.message),
                type: TOAST_TYPE_ERROR,
              }),
            );
          }
          const rejectionMessage = response.data.message;
          updateFileData(id, (current) => {
            const existingErrors: string[] = current.errors ?? [];
            return {
              ...current,
              error: rejectionMessage,
              uploadTriggered: true,
              progressCompleted: true,
              revisionRejected: true,
              errors: existingErrors.includes(rejectionMessage)
                ? existingErrors
                : [...existingErrors, rejectionMessage],
            };
          });
          return false;
        }
        throw new Error(response.data.message);
      }
    } catch (error: unknown) {
      if (showToast) {
        dispatch(
          showToastNotification({
            content: error instanceof Error ? untranslated(error.message) : t("Something went wrong"),
            type: TOAST_TYPE_ERROR,
          }),
        );
      }
      Promise.reject(error);
    } finally {
      updateFileData(id, { processing: false });
    }
  };

  const handleUpload = async (
    args: { id: number; name?: string },
    type?: string,
    customFieldOverrides?: Record<string, unknown>,
  ) => {
    const fileId = args.id;
    const file = fileUploadList[fileId - 1];

    if (!file || file.uploaded || file.processing || file.uploadTriggered) {
      return;
    }

    let name = file.name;
    let extension = "";

    if (name?.includes(".")) {
      const parts = name.split(".");
      const lastPart = parts.pop();
      if (lastPart !== undefined) {
        extension = lastPart;
      }
    }

    if (file.nomenclature) {
      // Reached only when the folder HAS a nomenclature rule (that is what fills `file.nomenclature`),
      // so the rule object is present. Erased assertion; `?.` here would change the emitted code.
      name = `${file.nomenclature.join(fileNomenclature!.separator)}${extension ? "." : ""}${extension}`;
    }

    const data = {
      id: file.fileId,
      name,
    };

    let templateWithAnswers: UploadTemplateSection[] = [];
    if (template?.length) {
      templateWithAnswers = _.cloneDeep(template).map((section) => {
        section.questionFields = section.questionFields!.map((q) => {
          const value = customFieldOverrides?.[q._id] ?? readRowField(file, q._id);
          if (value) {
            q.answer = { value } as QuestionField["answer"];
          }
          return q;
        });
        return section;
      });
    }

    const isValid = validateFormWithId(fileId, templateWithAnswers, formValidator);
    if (!isValid) return;

    const mergedForValidation = customFieldOverrides ? { ...file, ...customFieldOverrides } : file;
    const validationErrors = recalculateErrors(mergedForValidation);
    const blockingErrors = validationErrors.filter((message: string) => !REVISION_ERROR_SET.has(message));
    if (blockingErrors.length > 0) {
      updateFileData(fileId, { errors: validationErrors });
      return;
    }
    if (validationErrors.length > 0) {
      updateFileData(fileId, { errors: validationErrors });
    }

    if (type) {
      // `type` is one of the `uploadType.*` values, computed at runtime, so the write needs the
      // dynamic-key view of the payload. Erased.
      (data as Record<string, unknown>)[type] = true;
      updateFileData(fileId, (current) => {
        const next: UploadRowPatch = { ...current, [type]: true };
        next.errors = recalculateErrors(next);
        return next;
      });
    }

    if (file.progressCompleted) {
      const wasUploaded = await uploadEscrowFile(fileId, data);
      if (wasUploaded) {
        updateFileData(fileId, { uploadTriggered: true });
      }
    }
  };

  const saveNomenclature = (args: { id: number; name?: string }, nomenclatureArr: string[]) => {
    let extension = "";
    // KNOWN GAP, preserved verbatim: `name` is optional on the row and this dereferences it. `.pop()`
    // on a non-empty `split` result is always defined. Both assertions are erased.
    if (args.name!.includes(".")) {
      extension = args.name!.split(".").pop()!;
    }
    const filteredNomenclature = nomenclatureArr.filter((item) => item && item.trim() !== "");
    const composedName =
      filteredNomenclature.length > 0
        ? `${nomenclatureArr.join(fileNomenclature!.separator)}${extension ? "." : ""}${extension}`
        : "";
    // @ts-expect-error -- the nomenclature object passed here has every field optional while the callee requires them; a folder with no configured nomenclature reaches this call.
    const isCorrect = !!composedName && isFileNomenclatureCorrect(composedName, fileNomenclature);

    updateFileData(args.id, (current) => {
      const next: UploadRowPatch = {
        ...current,
        nomenclature: filteredNomenclature.length > 0 ? nomenclatureArr : [],
        isFileNomenclatureCorrect: isCorrect,
      };
      next.errors = recalculateErrors(next);
      return next;
    });

    if (isCorrect && fileUploadList[args.id - 1]?.progressCompleted) {
      updateNomenclature(fileUploadList[args.id - 1]?.fileId, composedName, args);
    }
  };

  const handleNomenclatureChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    args: { id: number; name?: string },
    field: unknown,
    index: number,
  ) => {
    const value = e.target.value;

    const existingNomenclature = fileUploadList[args.id - 1]?.nomenclature || [];
    const requiredLength = fileNomenclature?.fields?.length || 0;
    let nomenclatureArr = [...existingNomenclature];
    while (nomenclatureArr.length < requiredLength) {
      nomenclatureArr.push("");
    }
    nomenclatureArr[index] = value;
    saveNomenclature(args, nomenclatureArr);
  };

  const handleUploadComplete = async (file: UploadGridRow) => {
    const fileData = fileUploadList[file.id - 1];
    if (!fileData || fileData.processed) return;

    const updates: UploadRowPatch = { processed: true };

    if (!fileData.customFieldsSaved) {
      let fields: { _id: string; value: unknown }[] = [];

      if (fileData.customFieldsUpdated) {
        template?.forEach((section) => {
          section.questionFields!.forEach((field) => {
            const answer = readRowField(fileData, field._id);
            if (answer) {
              fields.push({ _id: field._id, value: answer });
            }
          });
        });
      }

      if (fields.length === 0 && fileData.matchedDirectImportContainer?.containerData && template?.length) {
        const containerData = fileData.matchedDirectImportContainer.containerData;
        template.forEach((section) => {
          section.questionFields?.forEach((field) => {
            const value = containerData[field.questionValue!];
            if (value !== undefined && value !== "") {
              fields.push({ _id: field._id, value });
            }
          });
        });
      }

      if (fields.length > 0) {
        await saveFormTemplate(file.id, fields, true);
        updates.customFieldsSaved = true;
      }
    }
    updateFileData(file.id, updates);
    if (fileData.nomenclature && !fileData.nomenclatureUpdated) {
      saveNomenclature(file, fileData.nomenclature);
    }

    if (readRowField(fileData, uploadType.UPLOAD_AS_NEW_FILE) && !fileData.uploaded) {
      handleUpload(file, uploadType.UPLOAD_AS_NEW_FILE);
    } else if (readRowField(fileData, uploadType.UPLOAD_AS_NEW_VERSION) && !fileData.uploaded) {
      handleUpload(file, uploadType.UPLOAD_AS_NEW_VERSION);
    } else if (readRowField(fileData, uploadType.UPLOAD_AS_NEW_VERSION_RETAIN_FIELDS) && !fileData.uploaded) {
      handleUpload(file, uploadType.UPLOAD_AS_NEW_VERSION_RETAIN_FIELDS);
    }
  };
  useEffect(() => {
    const allProcessed = visibleFiles.every((file) => {
      const item = fileUploadList[file.id - 1];
      if (!item || item.isDeleted) return true;

      return item.progressCompleted || item.error || item.isBulkEdit;
    });

    if (setEnableDone) {
      setEnableDone(allProcessed && pendingApiCalls === 0 && !bulkActionInProgress);
    }
  }, [fileUploadList, visibleFiles.length, pendingApiCalls, bulkActionInProgress]);

  useEffect(() => {
    fileUploadList.forEach((data: FileUploadItem, idx: number) => {
      const id = idx + 1;
      if (data.progressCompleted && !data.uploadCompleteProcessed) {
        handleUploadComplete({ ...data, id });
        updateFileData(id, { uploadCompleteProcessed: true });
      }
    });
  }, [fileUploadList]);

  const asArray = (v: unknown): unknown[] => {
    if (Array.isArray(v)) return v.filter((x: unknown) => x != null && x !== "");
    if (v == null) return [];
    if (typeof v === "string") return v ? [v] : [];
    if (typeof v === "object") {
      // A multi-select answer arrives either as the raw array or wrapped as `{ value }`. Asserted
      // inline rather than through a local, which would add a declaration to the emitted code.
      if (Array.isArray((v as { value?: unknown }).value)) return (v as { value?: unknown[] }).value!;
      if (typeof (v as { value?: unknown }).value === "string")
        return (v as { value?: string }).value ? [(v as { value?: string }).value!] : [];
      return Object.values(v).filter((x: unknown) => x != null && x !== "");
    }
    return [];
  };

  /**
   * `FormQuestionFields` declares every member of this payload optional — the widget chooses the shape
   * — so the handler is typed by that contract and the two members this grid needs are checked here:
   * `args` is the `extraParams={{ id }}` above, `eachField` the question the cell rendered.
   */
  const handleCustomFieldData: NonNullable<ComponentProps<typeof FormQuestionFields>["handleCustomFieldData"]> = ({
    args,
    eachField,
    answerValue,
  }) => {
    const rowId = args?.id;
    const fieldId = eachField?._id;
    if (typeof rowId !== "number" || fieldId === undefined) return;

    const value = eachField?.fieldType === "multi-select" ? asArray(answerValue) : answerValue;

    if (rowId === 0) {
      setBulkData((prev) => ({ ...prev, [fieldId]: value }));
    } else if (fileUploadList[rowId - 1]) {
      updateFileData(rowId, (data) => {
        const updated: UploadRowPatch = {
          ...data,
          [fieldId]: value,
          customFieldsUpdated: !data.progressCompleted ? true : data.customFieldsUpdated,
        };
        updated.errors = recalculateErrors(updated);
        return updated;
      });

      if (fileUploadList[rowId - 1]?.fileId || fileUploadList[rowId - 1]?.folderId) {
        const pending = pendingFieldsRef.current.get(rowId) || [];
        const existingIdx = pending.findIndex((p) => p._id === fieldId);
        if (existingIdx >= 0) {
          pending[existingIdx] = { _id: fieldId, value };
        } else {
          pending.push({ _id: fieldId, value });
        }
        pendingFieldsRef.current.set(rowId, pending);
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = setTimeout(() => setFlushTrigger((prev) => prev + 1), 500);
      }
    }
  };

  const saveFormTemplate = async (
    id: number,
    fieldsData: { _id: string; value: unknown }[] = [],
    progressCompleted?: boolean,
  ) => {
    const itemData: FileUploadItem = fileUploadList[id - 1] || {};
    const itemId = itemData.isFolder ? itemData.folderId : itemData.fileId;

    let isUploadComplete: boolean | undefined = true;
    if (!progressCompleted) {
      isUploadComplete = itemData.progressCompleted;
    }

    if (isUploadComplete && fieldsData.length && itemId) {
      await saveCustomFieldsData({
        id: itemId,
        formTemplateId,
        fieldsData,
        checkResidual: !batchId,
        isFolder: !!itemData.isFolder,
      });
    }
  };

  const updateNomenclature = async (id: string | undefined, name: string, args: { id: number }) => {
    updateFileData(args.id, { name: name, nomenclatureUpdated: true });
    if (id && !batchId) {
      setPendingApiCalls((prev) => prev + 1);

      axiosWrapper
        .post<UpdateFileNomenclatureResponse>(`${getBackendUrl()}/fileOperations/updateFileNomenclature`, {
          id,
          name,
        })
        .finally(() => {
          setPendingApiCalls((prev) => prev - 1);
        });
    }
  };

  const handleDeleteButtonClick = () => {
    if (selectedFileIds.length > 0) {
      setOpenDeleteConfirmationModal(true);
    }
  };

  const handleBulkUploadButtonClick = async () => {
    const selectedRows = selectedFileIds;
    if (selectedRows.length) {
      let allValid = true;
      for (const id of selectedRows) {
        const file = fileUploadList[id - 1];
        if (!file || !file.folder || !file.name) {
          continue;
        }
        // @ts-expect-error -- `string | null | undefined` is passed where a `string` is required; the revision label is absent until the series resolves.
        const nomenclatureValid = isFileNomenclatureCorrect(file.name, file.folder.fileNomenclature);

        if (!nomenclatureValid) {
          allValid = false;
          updateFileData(id, {
            error: "File nomenclature does not comply with the nomenclature rule for its Folder.",
            isFileNomenclatureCorrect: false,
          });
          continue;
        } else {
          updateFileData(id, {
            error: "",
            isFileNomenclatureCorrect: true,
          });
        }

        let templateWithAnswers: UploadTemplateSection[] = [];
        if (template && template.length) {
          templateWithAnswers = _.cloneDeep(template).map((section) => {
            const questionFields = section.questionFields!.map((question) => {
              const answer = readRowField(file, question._id);
              if (answer) {
                question["answer"] = { value: answer } as QuestionField["answer"];
              }
              return question;
            });
            section.questionFields = questionFields;
            return section;
          });
        }

        const isValid = validateFormWithId(id, templateWithAnswers, formValidator);
        if (!isValid) {
          allValid = false;
          updateFileData(id, { error: "Validation failed for custom fields." });
        }
      }

      if (allValid) {
        for (const id of selectedRows) {
          await handleUpload({
            id,
            name: fileUploadList[id - 1]?.name,
          });
        }
      }
    }
  };

  const collectCustomFieldIds = (template?: UploadTemplateSection[]) => {
    const ids = new Set<string>();
    template?.forEach((section) => {
      section.questionFields!.forEach((field) => ids.add(field._id));
    });
    return ids;
  };

  const buildBulkPayloads = (customFieldIds: Set<string>, bulkData: BulkEditRow) => {
    const data: Record<string, unknown> = {};
    const fieldsData: { _id: string; value: unknown }[] = [];

    customFieldIds.forEach((fieldId) => {
      if (bulkData[fieldId] === undefined) return;
      data[fieldId] = bulkData[fieldId];
      fieldsData.push({ _id: fieldId, value: bulkData[fieldId] });
    });

    return { data, fieldsData };
  };

  const updateTemplateWithAnswers = (template: UploadTemplateSection[] | undefined, safeData: UploadRowFields) => {
    return _.cloneDeep(template)!.map((section) => {
      section.questionFields = section.questionFields!.map((q) => {
        if (safeData[q._id]) q.answer = { value: safeData[q._id] } as QuestionField["answer"];
        return q;
      });
      return section;
    });
  };

  const applyBulkToItem = async ({
    index,
    data,
    fieldsData,
    template,
    dispatch,
    saveFormTemplate,
    validateFormWithId,
    formValidator,
  }: {
    index: number;
    data: Record<string, unknown>;
    fieldsData: { _id: string; value: unknown }[];
    template?: UploadTemplateSection[];
    /** All three are the closures defined in this component, forwarded rather than captured. */
    dispatch: ReturnType<typeof useDispatch>;
    saveFormTemplate: (
      id: number,
      fieldsData?: { _id: string; value: unknown }[],
      progressCompleted?: boolean,
    ) => Promise<void>;
    validateFormWithId: (id: number, template: UploadTemplateSection[], formValidator: unknown) => boolean;
    formValidator: FilesGridProps["formValidator"];
  }) => {
    const existingData: FileUploadItem = fileUploadList[index] || {};
    const merged = { ...existingData, ...data, customFieldsUpdated: true } as UploadRowFields;
    const errors = recalculateErrors(merged);
    const safeData = { ...data, customFieldsUpdated: true, errors };

    dispatch(updateFileUploadData({ index, data: safeData }));
    await saveFormTemplate(index + 1, fieldsData);

    const templateWithAnswers = updateTemplateWithAnswers(template, merged);
    validateFormWithId(index + 1, templateWithAnswers, formValidator);
  };

  const resetBulkState = (
    setBulkData: React.Dispatch<React.SetStateAction<BulkEditRow>>,
    setIsBulk: React.Dispatch<React.SetStateAction<boolean>>,
    setBulkActionType: React.Dispatch<React.SetStateAction<string | null>>,
  ) => {
    setBulkData({ id: 0, progress: 0, name: "", isBulkEdit: true });
    setIsBulk(false);
    setBulkActionType(null);
  };

  const getUploadTypeFromValue = (value: string | null) => {
    if (value === "newFile") return uploadType.UPLOAD_AS_NEW_FILE;
    if (value === "newVersion") return uploadType.UPLOAD_AS_NEW_VERSION;
    if (value === "newVersionRetainFields") return uploadType.UPLOAD_AS_NEW_VERSION_RETAIN_FIELDS;
    return null;
  };

  const isFileEligibleForBulkAction = (file: FileUploadItem) => {
    if (file.isDeleted) return false;
    if (file.duplicateFileStatus?.isDuplicateContainer || file.duplicateFileStatus?.noMatchingContainer) return false;
    if (file.nomenclature?.length && file.isFileNomenclatureCorrect && !batchId) return false;

    const isDuplicate = file.isDuplicate || file.duplicateFileStatus?.found;
    if (isDuplicate) {
      if (batchId) {
        return !(!file.isFileNomenclatureCorrect || file.nomenclature?.length);
      } else {
        return file.isFileNomenclatureCorrect;
      }
    }
    return false;
  };

  const toggleBulkUpdateButton = async () => {
    if (!isBulk) {
      setIsBulk(true);
      setSelectedFileIds([]);
      setBulkActionApplied(false);
      return;
    }

    if (bulkActionType) {
      setBulkActionApplied(true);
    }

    const customFieldIds = collectCustomFieldIds(template);

    setBulkActionInProgress(true);

    try {
      const { data: bulkCustomFieldData, fieldsData } = buildBulkPayloads(customFieldIds, bulkData);

      if (fieldsData.length > 0) {
        await Promise.allSettled(
          fileUploadList.map((_, index) =>
            applyBulkToItem({
              index,
              data: bulkCustomFieldData,
              fieldsData,
              template,
              dispatch,
              saveFormTemplate,
              // @ts-expect-error -- the template callback is declared over `ValidatableSection[]` but the grid supplies its own row type; the two template shapes disagree.
              validateFormWithId,
              formValidator,
            }),
          ),
        );
      }

      if (bulkActionType) {
        const actionTypeKey = getUploadTypeFromValue(bulkActionType);
        if (actionTypeKey) {
          await Promise.allSettled(
            fileUploadList
              .map((item, index) => ({ item, index }))
              .filter(({ item }) => isFileEligibleForBulkAction(item))
              .map(({ item, index }) =>
                handleUpload({ id: index + 1, name: item.name }, actionTypeKey, bulkCustomFieldData),
              ),
          );
        }
      }
    } finally {
      setBulkActionInProgress(false);
    }

    resetBulkState(setBulkData, setIsBulk, setBulkActionType);
    setSelectedFileIds([]);
    setGridRefreshKey((prev) => prev + 1);
  };

  const successDeleteToast = () => {
    dispatch(
      showToastNotification({
        content: t("File(s) deleted successfully."),
        type: TOAST_TYPE_SUCCESS,
      }),
    );
  };

  const handleDeleteModalClose = () => {
    setOpenDeleteConfirmationModal(false);
  };

  function removeFilesByIds(ids: number[]) {
    ids.forEach((id) => {
      if (typeof id === "number") {
        dispatch(removeFileUploadData(id - 1));
      }
    });
  }

  const handleDeleteSuccess = (indexes: number[]) => {
    setSelectedFileIds([]);
    handleDeleteModalClose();
    removeFilesByIds(indexes);
    successDeleteToast();
  };

  const onDeleteFiles = () => {
    if (!batchId) {
      visibleFiles
        .filter((data) => data.id != 0 && selectedFileIds.includes(data.id))
        .forEach((file) => {
          const controller = fileUploadList[file.id - 1]?.controller;
          if (controller) {
            (Array.isArray(controller) ? controller : [controller]).forEach((item) => item.abort());
          }
        });
    }
  };

  const getFilesGrid = () => (
    <div className="overflow-auto h-full">
      <FilesTable
        key={gridRefreshKey}
        fileUploadList={visibleFiles}
        selectedFileIds={selectedFileIds}
        setSelectedFileIds={setSelectedFileIds}
        // `fileNomenclature` is declared required on `FilesTable` and dereferenced unconditionally
        // there (see its own note); it is optional here only because `defaultProps` below declares
        // it so. `template` is forwarded as-is: `FilesTable` declares it optional and guards it, so
        // the assertion that used to sit here was covering for the React-18-only `defaultProps`
        // fallback rather than for anything that is actually always set.
        template={template}
        gridNomenclatureTemplate={gridNomenclatureTemplate}
        progressTemplate={progressTemplate}
        gridNameTemplate={gridNameTemplate}
        gridCustomFieldTemplate={gridCustomFieldTemplate}
        gridUploadActionTemplate={gridUploadActionTemplate}
        fileNomenclature={fileNomenclature!}
        isDirectImportFlow={isDirectImportFlow}
        gridLocationTemplate={gridLocationTemplate}
      />
    </div>
  );

  return (
    <div className="h-full overflow-auto flex flex-col" id="formData">
      {isDirectImportFlow && reconciliationCounts?.hasGaps && (
        <div className="px-4 pt-4">
          <ReconciliationBanner
            emptyContainersCount={reconciliationCounts.emptyContainersCount}
            unmatchedFilesCount={reconciliationCounts.unmatchedFilesCount}
            onViewLog={() => setShowReconLogModal(true)}
          />
        </div>
      )}
      <div className="p-4 flex gap-4">
        {!isFileVersionUploading && (
          <ButtonComponent
            onClick={handleDeleteButtonClick}
            buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
            disabled={disableDeleteButton}
          >
            <span className="flex items-center gap-2">
              <MdDeleteOutline className="h-4" />
              {t("Delete")}
            </span>
          </ButtonComponent>
        )}
        {!isFileVersionUploading && template && template.length ? (
          !isBulk ? (
            <ButtonComponent
              onClick={toggleBulkUpdateButton}
              buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
              disabled={disableBulkEditButton}
            >
              <span className="flex items-center gap-2">
                <MdOutlineEdit className="h-4" />
                {t("Bulk Edit")}
              </span>
            </ButtonComponent>
          ) : (
            <ButtonComponent
              onClick={toggleBulkUpdateButton}
              buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
              disabled={disableBulkEditButton}
            >
              <span className="flex items-center gap-2">
                <FaRegSave className="h-4" />
                {t("Update All")}
              </span>
            </ButtonComponent>
          )
        ) : null}
        {batchId ? (
          <ButtonComponent
            onClick={handleBulkUploadButtonClick}
            buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
            disabled={disableBulkUploadButton}
          >
            <span className="flex items-center gap-2">
              <img src={Upload} className="h-4" />
              {"Bulk Upload"}
            </span>
          </ButtonComponent>
        ) : null}
      </div>
      {getFilesGrid()}
      {showReconLogModal && reconciliationRows && (
        <ReconciliationLogModal
          isVisible={showReconLogModal}
          onClose={() => setShowReconLogModal(false)}
          emptyContainers={reconciliationRows.emptyContainers}
          unmatchedFiles={reconciliationRows.unmatchedFiles}
        />
      )}
      {openDeleteConfirmationModal && (
        <div style={{ marginInlineStart: "-2.5rem" }}>
          <FileDelete
            // `null` in the escrow flow, where `useFileDelete` would fall back to each row's own
            // `spaceId`. Erased assertion at the one boundary that declares it non-null.
            workspaceId={workspaceId!}
            selectedFiles={
              // A grid row's `id` is its 1-based row number and its `hierarchyId`/`spaceId` are plain
              // strings, while `DeleteTargetRow` (useFileDelete.tsx:17) is the Zod `SelectedFileItem`,
              // whose same-named members are branded ObjectIds — so no upload row can satisfy it. The
              // hook reads only `hierarchyId` (set just below), `spaceId`, `resourceType` and `name`,
              // all of which these rows carry; widening `DeleteTargetRow` is the real fix and belongs
              // in that file.
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- upload rows carry every member useFileDelete reads; only the branded ObjectId declarations in useFileDelete.tsx:17 make them unassignable
              visibleFiles
                .filter((data) => data.id != 0 && selectedFileIds.includes(data.id))
                .map((file) => {
                  const item = fileUploadList[file.id - 1];
                  if (item?.isFolder) {
                    file.hierarchyId = item.folderId;
                  } else if (item) {
                    file.hierarchyId = item.fileId;
                  }
                  return file;
                  // The intersection keeps the source arm, without which TS rejects the conversion
                  // outright (TS2352) — the two row shapes really are that far apart.
                }) as UploadGridRow[] & ComponentProps<typeof FileDelete>["selectedFiles"]
            }
            onCloseModal={handleDeleteModalClose}
            onDeleteSuccess={() => handleDeleteSuccess(selectedFileIds)}
            isFileUpload
            onDeleteFiles={onDeleteFiles}
          />
        </div>
      )}
    </div>
  );
};

FilesGrid.defaultProps = {
  template: [],
  formTemplateId: undefined,
  workspaceId: undefined,
  setEnableDone: undefined,
  fileNomenclature: { fields: [], separator: "-" },
  batchId: undefined,
  formValidator: undefined,
  isDirectImportFlow: false,
  directImportContainers: [],
};

export default FilesGrid;
